/**
 * A tile's settings, as the Configure panel edits them — and how they become
 * a tile again.
 *
 * PURE, like `tile-editing.ts`. The panel holds ONE draft of these, so every
 * section (title, report, filters, chart) edits the same object and Apply is a
 * single write into the dashboard's draft definition — never four separate
 * edits that could each half-apply.
 *
 * The REPORT a tile shows has three states:
 *
 *  - `linked`: a custom report, by id. Its question is the report's and is
 *    changed on the report's own page, never here; the tile may only add
 *    filters on top.
 *  - `inline`: a report built on this dashboard, carried on the tile.
 *  - `none`: a new tile whose report has not been picked or built yet.
 */
import type {
  DashboardDefinition,
  DashboardTile,
  FilterNode,
  ReportModel,
  ReportSpec,
  TileView,
} from '@clockwise/semantic-core';
import {
  filterValueType,
  isDashboardReport,
  isFilterGroup,
  isLinkedReport,
  reportPeriodPreset,
  validateFilters,
  vizAvailability,
} from '@clockwise/semantic-core';
import { saysSomething, withTileFilters } from './filters';
import { nextPosition, nextTileId, reportTileSize } from './tile-editing';

export type TileSource =
  | { kind: 'linked'; reportId: string }
  | { kind: 'inline'; spec: ReportSpec }
  | { kind: 'none' };

export interface TileSettings {
  /** A number tile keeps its own shape: one measure, drawn as a number. */
  kind: 'report' | 'metric';
  /** As typed. Empty means "the report's own name" for a linked tile. */
  title: string;
  source: TileSource;
  /** A linked tile's own filters, ANDed onto the report's. Empty otherwise. */
  filters: FilterNode[];
  view: TileView;
}

/** A new report tile: nothing picked yet, drawn as a table until told otherwise. */
export function newReportSettings(): TileSettings {
  return {
    kind: 'report',
    title: '',
    source: { kind: 'none' },
    filters: [],
    view: { viz: 'table', stacked: false },
  };
}

/** The settings a placed tile already has. */
export function settingsOf(tile: DashboardTile): TileSettings {
  if (tile.type === 'metric') {
    return {
      kind: 'metric',
      title: tile.title ?? '',
      source: { kind: 'inline', spec: tile.spec },
      filters: [],
      view: { viz: 'metric', stacked: false },
    };
  }
  if (isLinkedReport(tile)) {
    return {
      kind: 'report',
      title: tile.title ?? '',
      source: { kind: 'linked', reportId: tile.reportId },
      filters: [...(tile.filters ?? [])],
      view: tile.view,
    };
  }
  if (isDashboardReport(tile)) {
    return {
      kind: 'report',
      title: tile.title ?? '',
      source: { kind: 'inline', spec: tile.spec },
      filters: [],
      view: tile.view,
    };
  }
  return newReportSettings();
}

/**
 * Point the tile at a different custom report.
 *
 * The title, chart and the tile's own filters CARRY OVER — the author is
 * swapping what the tile shows, not starting again. A filter the new report's
 * subject area cannot run is DROPPED and named, rather than kept to fail every
 * query; a chart the new report cannot be drawn as falls back to a table.
 */
export function withLinkedReport(
  settings: TileSettings,
  report: { id: string; spec: ReportSpec },
  model?: ReportModel,
): { settings: TileSettings; dropped: FilterNode[] } {
  const kept: FilterNode[] = [];
  const dropped: FilterNode[] = [];
  for (const node of settings.filters) {
    if (!model || runsOn(model, node)) kept.push(node);
    else dropped.push(node);
  }
  // Unchecked is not the same as fine: with no catalog entry to ask, the one
  // chart every report can be drawn as.
  const drawable = !!model && chartAvailable(model, report.spec, settings.view.viz);
  return {
    settings: {
      ...settings,
      kind: 'report',
      source: { kind: 'linked', reportId: report.id },
      filters: kept,
      view: drawable ? settings.view : { viz: 'table', stacked: false },
    },
    dropped,
  };
}

/**
 * Can this tile filter follow the tile onto `model`?
 *
 * A rule that says something must validate there. One that says nothing yet —
 * a chip with no value, or a paused one — only needs its FIELD to exist there:
 * validating it would fail for the missing value, not for the new report, and
 * report it as a filter the report "can't use".
 */
function runsOn(model: ReportModel, node: FilterNode): boolean {
  if (isFilterGroup(node)) return validateFilters(model, [node]).ok;
  // Read before the guard: `saysSomething` narrows its false branch to nothing.
  const { field } = node;
  return saysSomething(node)
    ? validateFilters(model, [node]).ok
    : filterValueType(model, field) !== null;
}

/**
 * "Build here, starting from this report": the linked report's question,
 * copied onto the tile.
 *
 * The tile's own filters are folded INTO the copy — they were narrowing what
 * the tile showed, and dropping them would widen it the moment it stopped
 * being linked. The report's name becomes the title unless one was typed,
 * since a dashboard report has no saved report to borrow a name from.
 */
export function withInlineCopy(
  settings: TileSettings,
  report: { spec: ReportSpec; name: string },
): TileSettings {
  return {
    ...settings,
    source: { kind: 'inline', spec: withTileFilters(report.spec, settings.filters) },
    filters: [],
    title: settings.title.trim() === '' ? report.name : settings.title,
  };
}

/** A report built (or rebuilt) in the builder, with the chart it was previewed in. */
export function withBuiltReport(settings: TileSettings, spec: ReportSpec): TileSettings {
  return {
    ...settings,
    source: { kind: 'inline', spec },
    filters: [],
    view: settings.kind === 'metric' ? settings.view : { viz: spec.viz, stacked: spec.stacked },
  };
}

/** Can `spec` be drawn as `viz`? A listing only ever draws as a table. */
export function chartAvailable(
  model: ReportModel,
  spec: ReportSpec,
  viz: TileView['viz'],
): boolean {
  if (spec.kind === 'detail') return viz === 'table';
  return vizAvailability(model, spec).get(viz)?.ok ?? false;
}

/** Why Apply is not available yet, or `null`. */
export function settingsRefusal(settings: TileSettings): string | null {
  if (settings.source.kind === 'none') return 'Pick a custom report or build one.';
  return null;
}

/** Filters that say something — a chip added and never filled in narrows nothing. */
function meaningful(nodes: readonly FilterNode[]): FilterNode[] {
  return nodes.filter((node) => isFilterGroup(node) || saysSomething(node));
}

/**
 * Write the settings into the definition — replacing the tile they came from,
 * or placing a new one when `tileId` is null.
 *
 * A replaced tile keeps its id and its place, whatever its kind became: the
 * author reconfigured a tile, they did not delete one and add another.
 *
 * An inline spec is stored over the DASHBOARD's period, and in the chart the
 * tile shows: the builder's period only drove its preview, and a spec whose
 * `viz` disagreed with its tile's `view` would be two answers to one question.
 */
export function applyTileSettings(
  definition: DashboardDefinition,
  tileId: string | null,
  settings: TileSettings,
  /** Used when an inline report has no title typed: the report described in words. */
  fallbackTitle = '',
): DashboardDefinition {
  const existing = tileId ? definition.tiles.find((t) => t.id === tileId) : undefined;
  const id =
    existing?.id ?? nextTileId(definition.tiles, settings.kind === 'metric' ? 'metric' : 'report');
  const layout =
    existing?.layout ?? nextPosition(definition.tiles, reportTileSize(settings.view.viz));
  const title = settings.title.trim();
  const { source, view } = settings;

  const stored = (spec: ReportSpec, viz: TileView): ReportSpec => ({
    ...spec,
    viz: viz.viz,
    stacked: viz.stacked,
    period: definition.period.preset ? reportPeriodPreset(definition.period.preset) : undefined,
    from: definition.period.from,
    to: definition.period.to,
  });

  let tile: DashboardTile;
  if (source.kind === 'none') return definition;
  if (settings.kind === 'metric' && source.kind === 'inline') {
    tile = {
      id,
      type: 'metric',
      layout,
      title: title === '' ? undefined : title,
      spec: stored(source.spec, { viz: 'metric', stacked: false }),
    };
  } else if (source.kind === 'linked') {
    const filters = meaningful(settings.filters);
    tile = {
      id,
      type: 'report',
      layout,
      title: title === '' ? undefined : title,
      reportId: source.reportId,
      view,
      ...(filters.length > 0 ? { filters } : {}),
    };
  } else {
    tile = {
      id,
      type: 'report',
      layout,
      title: title === '' ? fallbackTitle || 'Report' : title,
      spec: stored(source.spec, view),
      view,
    };
  }

  return existing
    ? { ...definition, tiles: definition.tiles.map((t) => (t.id === id ? tile : t)) }
    : { ...definition, tiles: [...definition.tiles, tile] };
}
